import React from 'react';
import {CheckoutSettingsModel} from '../../../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';
import {TaxModel} from '../../../../../domain/models/checkout/CartSummary/Tax.model';
import {ILocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {
  getTaxName,
  isFullTaxExempt,
  isPartialTaxExempt,
  formatSignedAmount,
} from '../../../../../domain/utils/total.util';
import {ExemptTax} from './ExemptTax';

export const getTaxIncludedWithPartialExemptionText = ({
  tax,
  checkoutSettings,
  localeKeys,
  moveCheckoutToViewerContext,
}: {
  tax: TaxModel;
  checkoutSettings: CheckoutSettingsModel;
  localeKeys: ILocaleKeys;
  moveCheckoutToViewerContext: boolean;
}): string =>
  localeKeys.checkout.order_summary.totals.includesTaxDisclaimer({
    taxName: getTaxName({
      taxName: tax.name,
      checkoutSettings,
      localeKeys,
      moveCheckoutToViewerContext,
    }),
    taxAmount: tax.amount.formattedAmount,
  });

export const getSingleTaxIncludedText = ({
  tax,
  checkoutSettings,
  localeKeys,
  moveCheckoutToViewerContext,
}: {
  tax: TaxModel;
  checkoutSettings: CheckoutSettingsModel;
  localeKeys: ILocaleKeys;
  moveCheckoutToViewerContext: boolean;
}): string => {
  if (isFullTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.taxExemption.label();
  }

  return localeKeys.checkout.order_summary.totals.includesTaxDisclaimer({
    taxAmount: tax.amount.formattedAmount,
    taxName: getTaxName({
      taxName: tax.name,
      checkoutSettings,
      localeKeys,
      moveCheckoutToViewerContext,
    }),
  });
};

export const getTaxIncludedRowText = ({
  tax,
  checkoutSettings,
  localeKeys,
  hasOneTaxWithPartialExemption,
  moveCheckoutToViewerContext,
}: {
  tax: TaxModel;
  checkoutSettings: CheckoutSettingsModel;
  localeKeys: ILocaleKeys;
  hasOneTaxWithPartialExemption: boolean;
  moveCheckoutToViewerContext: boolean;
}): string => {
  if (hasOneTaxWithPartialExemption) {
    return getTaxIncludedWithPartialExemptionText({
      tax,
      checkoutSettings,
      localeKeys,
      moveCheckoutToViewerContext,
    });
  }

  if (isFullTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.taxExemptionBreakdown.label({
      taxName: getTaxName({
        taxName: tax.name,
        checkoutSettings,
        localeKeys,
        moveCheckoutToViewerContext,
      }),
      taxAmount: tax.exemptAmount.formattedAmount,
    });
  }

  if (isPartialTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.partialTaxExemptionBreakdown.label({
      taxName: getTaxName({
        taxName: tax.name,
        checkoutSettings,
        localeKeys,
        moveCheckoutToViewerContext,
      }),
      taxAmount: tax.amount.formattedAmount,
    });
  }

  return localeKeys.checkout.order_summary.totals.taxGroupsDisclaimer.breakdown({
    taxName: getTaxName({
      taxName: tax.name,
      checkoutSettings,
      localeKeys,
      moveCheckoutToViewerContext,
    }),
    taxAmount: tax.amount.formattedAmount,
  });
};

export const getTaxValue = ({
  tax,
  isTaxIncludedInPrice,
  isRTL,
  localeKeys,
}: {
  tax: TaxModel;
  isTaxIncludedInPrice: boolean;
  isRTL: boolean;
  localeKeys: ILocaleKeys;
}) => {
  if (isFullTaxExempt(tax)) {
    return isTaxIncludedInPrice
      ? formatSignedAmount({amount: tax.originalAmount.formattedAmount, isRTL})
      : localeKeys.checkout.order_summary.totals.fullTaxExemption.label();
  }

  if (isPartialTaxExempt(tax)) {
    return isTaxIncludedInPrice
      ? formatSignedAmount({amount: tax.exemptAmount.formattedAmount, isRTL})
      : () => <ExemptTax originalAmount={tax.originalAmount.formattedAmount} amount={tax.amount.formattedAmount} />;
  }
  return tax.amount.formattedAmount;
};
